/**
 * PricingRuleStep - Step 4 of SMB Vendor Cost Import
 *
 * Configure how costs and prices should be calculated
 * Shows live preview of pricing calculation
 */

import React, { useState, useMemo } from 'react'
import { Space, Card, Alert, Button, Select, Radio, InputNumber, Typography, Divider } from 'antd'
import { CalculatorOutlined } from '@ant-design/icons'
import type { StepProps, PriceCalculationMethod, PricingBasis, RoundingRule } from './types'

const { Title, Text } = Typography

/**
 * Pricing basis options - generic field types
 */
const PRICING_BASIS_OPTIONS: { value: PricingBasis; label: string }[] = [
  { value: 'cost', label: 'Cost' },
  { value: 'map', label: 'Minimum Advertised Price' },
  { value: 'msrp', label: 'MSRP' },
]

/**
 * Rounding rule options
 */
const ROUNDING_RULE_OPTIONS: { value: RoundingRule; label: string }[] = [
  { value: 'nearest_penny', label: 'To the nearest penny' },
  { value: 'nearest_dollar', label: 'To the nearest dollar' },
  { value: 'up_penny', label: 'Round up to the nearest penny' },
  { value: 'up_dollar', label: 'Round up to the nearest dollar' },
]

/**
 * Step 4: Configure pricing rules
 */
export const PricingRuleStep: React.FC<StepProps> = ({ state, onNext, onBack, onCancel }) => {
  // Get available pricing basis fields from vendor mappings (fields that have columns selected)
  // vendorMappings is now an object like { Cost: ['col1'], MAP: ['col2'], ... }
  const availablePricingBasisFields = useMemo(() => {
    const available: PricingBasis[] = []
    const mappings = state.vendorMappings as any
    if (mappings?.Cost?.length > 0) available.push('cost')
    if (mappings?.MAP?.length > 0) available.push('map')
    if (mappings?.MSRP?.length > 0) available.push('msrp')
    return available
  }, [state.vendorMappings])

  // Default to 'cost' if available, otherwise first available option
  const defaultPricingBasis: PricingBasis = availablePricingBasisFields.includes('cost')
    ? 'cost'
    : availablePricingBasisFields[0] || 'cost'

  const [pricingBasis, setPricingBasis] = useState<PricingBasis>(defaultPricingBasis)
  const [priceCalculationMethod, setPriceCalculationMethod] = useState<PriceCalculationMethod>(
    state.priceCalculationMethod || 'formula'
  )
  const [costMultiplier, setCostMultiplier] = useState(state.costMultiplier || 1.2)
  const [costAdder, setCostAdder] = useState(state.costAdder || 0)
  const [roundingRule, setRoundingRule] = useState<RoundingRule>(
    state.roundingRule || 'nearest_penny'
  )

  // Calculate example price with rounding
  const exampleCost = 22.5
  const examplePrice = useMemo(() => {
    let price: number
    if (priceCalculationMethod === 'vendor_field') {
      // Would use vendor field value - just show example
      price = pricingBasis === 'map' ? 35.0 : pricingBasis === 'msrp' ? 39.99 : 27.0
    } else {
      price = exampleCost * costMultiplier + costAdder
    }

    // Apply rounding if formula mode
    if (priceCalculationMethod === 'formula') {
      switch (roundingRule) {
        case 'nearest_penny':
          price = Math.round(price * 100) / 100
          break
        case 'nearest_dollar':
          price = Math.round(price)
          break
        case 'up_penny':
          price = Math.ceil(price * 100) / 100
          break
        case 'up_dollar':
          price = Math.ceil(price)
          break
      }
    }

    return price
  }, [priceCalculationMethod, pricingBasis, costMultiplier, costAdder, roundingRule])

  const handleNext = () => {
    onNext({
      pricingBasis,
      priceCalculationMethod,
      costMultiplier,
      costAdder,
      roundingRule,
    })
  }

  // Get display label for current pricing basis
  const selectedFieldLabel = PRICING_BASIS_OPTIONS.find(o => o.value === pricingBasis)?.label || pricingBasis

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Instructions */}
      <Card>
        <Title level={4}>Step 4: Configure Pricing Rules</Title>
        <Text>
          Choose which vendor field to use and how to calculate your selling prices. The
          preview shows how your pricing formula will work.
        </Text>
      </Card>

      {/* Price Calculation - Single consolidated card */}
      <Card title="Price Calculation">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Vendor field dropdown - always visible */}
          <div>
            <Text>Which vendor field contains the value you want to use?</Text>
            <Select
              value={pricingBasis}
              onChange={setPricingBasis}
              style={{ width: '100%', maxWidth: '400px', marginTop: '8px', display: 'block' }}
            >
              {PRICING_BASIS_OPTIONS.filter((opt) =>
                availablePricingBasisFields.includes(opt.value)
              ).map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Divider style={{ margin: '12px 0', borderColor: '#d9d9d9' }} />

          {/* How to use the selected field */}
          <div>
            <Text>How should this value be used?</Text>
            <Radio.Group
              value={priceCalculationMethod}
              onChange={(e) => setPriceCalculationMethod(e.target.value)}
              style={{ marginTop: '12px', display: 'block' }}
            >
              <Space direction="vertical">
                {/* Option 1: Use vendor field directly */}
                <Radio value="vendor_field" disabled={availablePricingBasisFields.length === 0}>
                  Use the vendor field value directly as my price
                </Radio>

                {/* Option 2: Calculate using formula */}
                <Radio value="formula">Calculate my price using this field as the base</Radio>
                {priceCalculationMethod === 'formula' && (
                  <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                    <Space>
                      <Text>{selectedFieldLabel} x</Text>
                      <InputNumber
                        value={costMultiplier}
                        onChange={(value) => setCostMultiplier(value || 1)}
                        min={0}
                        max={10}
                        step={0.1}
                        precision={2}
                        style={{ width: '100px' }}
                      />
                      <Text>+</Text>
                      <InputNumber
                        value={costAdder}
                        onChange={(value) => setCostAdder(value || 0)}
                        min={0}
                        max={1000}
                        step={1}
                        precision={2}
                        prefix="$"
                        style={{ width: '120px' }}
                      />
                    </Space>
                  </div>
                )}
              </Space>
            </Radio.Group>
          </div>

          {/* Rounding - only show when formula mode, left-aligned */}
          {priceCalculationMethod === 'formula' && (
            <>
              <Divider style={{ margin: '12px 0', borderColor: '#d9d9d9' }} />
              <div>
                <Text>How should the final price be rounded?</Text>
                <Select
                  value={roundingRule}
                  onChange={setRoundingRule}
                  style={{ width: '100%', maxWidth: '400px', marginTop: '8px', display: 'block' }}
                >
                  {ROUNDING_RULE_OPTIONS.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </>
          )}

          {availablePricingBasisFields.length === 0 && (
            <Alert
              message="No vendor price fields available"
              description="Your vendor file doesn't have Cost, MAP, or MSRP fields mapped. Please go back and map at least one pricing field."
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Card>

      {/* Live Preview */}
      <Card style={{ background: '#e6f7ff', borderColor: '#1890ff' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <CalculatorOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            <Text strong>Live Preview</Text>
          </Space>
          <Divider style={{ margin: '8px 0' }} />
          {priceCalculationMethod === 'formula' ? (
            <>
              <Text>
                Example: ${exampleCost.toFixed(2)} {selectedFieldLabel} x {costMultiplier} + ${costAdder.toFixed(2)} = $
                {(exampleCost * costMultiplier + costAdder).toFixed(2)}
              </Text>
              <Text strong style={{ color: '#1890ff' }}>
                Rounded ({ROUNDING_RULE_OPTIONS.find(o => o.value === roundingRule)?.label}): ${examplePrice.toFixed(2)} final price
              </Text>
            </>
          ) : (
            <Text>
              Example: Vendor {selectedFieldLabel} ($
              {examplePrice.toFixed(2)}) will be copied directly as your price
            </Text>
          )}
        </Space>
      </Card>

      {/* Action buttons */}
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button size="large" onClick={onBack}>
              Back
            </Button>
            <Button size="large" onClick={onCancel}>
              Cancel
            </Button>
          </Space>
          <Button type="primary" size="large" onClick={handleNext}>
            Next: Process Files
          </Button>
        </Space>
      </Card>
    </Space>
  )
}
